/**
 * Stim-day copy for /demo/fertility/when-to-see-a-doctor — one entry per day (1–14).
 * All UI reads from fertilityWhenToSeeDoctorDayData[selectedDay]; do not anchor-collapse days.
 */

/** @typedef {{ source: string, title: string, author: string, url: string, min: number }} StimArticle */

/**
 * @typedef {{
 *   novaGuidance: string,
 *   headerSubtitle: string,
 *   phaseLabel: string,
 *   phaseSummary: string,
 *   bubbleVariant: 'soft'|'trigger'|'recovery',
 *   bodyDoing: string[],
 *   normalToFeel: string[],
 *   easeTips: string[],
 *   ohssLevel: 'low'|'watch'|'elevated',
 *   ohssContext: { title: string, body: string },
 *   signsWorthNoting: string[],
 *   motivationalQuote: string, // shown under "Tip" (left column); unique per stim day
 *   numbers: { label: string, value: string }[],
 *   articles: StimArticle[],
 * }} StimDayEntry
 */

/** @type {Record<number, StimDayEntry>} */
export const fertilityWhenToSeeDoctorDayData = {
  1: {
    novaGuidance:
      'Stim day 1 — your first injection is tonight. No physical changes yet, but the cycle has officially started.',
    headerSubtitle: 'Your first injection tonight — stimulation begins.',
    phaseLabel: 'Day 1 — the cycle begins',
    phaseSummary:
      "Tonight you give your first gonadotropin injection. Your ovaries are quiet right now — follicles haven't started responding yet. By day 3–4 you'll notice subtle changes as they begin to recruit.",
    bubbleVariant: 'soft',
    bodyDoing: [
      'Your ovaries are at baseline — no follicular activity yet',
      'The injections signal your ovaries to begin growing multiple follicles',
      'Your first monitoring scan is likely day 3–5 to check early response',
      'Estradiol starts very low and will rise as follicles develop',
    ],
    normalToFeel: [
      'Injection site redness or mild bruising — very common',
      'Feeling anxious or emotional — completely normal',
      'No physical stim symptoms yet — that comes later',
      'Mild headache from hormonal changes is possible',
    ],
    ohssLevel: 'low',
    ohssContext: {
      title: 'OHSS risk today — low',
      body:
        "No OHSS risk on day 1. Your ovaries haven't responded yet. Risk builds gradually as follicles grow over the coming days.",
    },
    signsWorthNoting: [
      'Injection site irritation — cold pack and rotate sites',
      'Allergic reaction to medication → call clinic immediately',
    ],
    motivationalQuote: 'Day 1 is the beginning of something. Let yourself feel that.',
    easeTips: [
      'Pick a consistent injection time tonight — small routines make the first nights easier.',
      'Gather swabs, syringes, and a sharps container in one spot so you are not hunting at bedtime.',
      'Have a light snack ready if your clinic is fine with food around shots — steady energy helps nerves.',
    ],
    numbers: [
      { label: 'Starting E2', value: '<100 pg/mL — baseline' },
      { label: 'LH', value: '<5 mIU/mL — should stay low' },
      { label: 'Follicles', value: 'Not yet measurable' },
    ],
    articles: [
      {
        source: 'ReproductiveFacts.org',
        title: 'In Vitro Fertilization Treatment Journey',
        author: 'ASRM Practice Committee',
        url: 'https://www.reproductivefacts.org/patient-journeys/in-vitro-fertilization-treatment/',
        min: 6,
      },
      {
        source: 'CCRM Fertility',
        title: 'IVF Process Day by Day',
        author: 'CCRM Clinical Team',
        url: 'https://www.ccrmivf.com/fertility/ivf-process/',
        min: 5,
      },
      {
        source: 'CCRM Fertility',
        title: 'Controlled Ovarian Stimulation',
        author: 'CCRM Clinical Team',
        url: 'https://www.ccrmivf.com/fertility/controlled-ovarian-hyperstimulation/',
        min: 4,
      },
    ],
  },
  2: {
    novaGuidance:
      'Stim day 2 — second injection tonight. Still early. Your ovaries are beginning to receive the signal.',
    headerSubtitle: 'Second night of injections — hormones are ramping.',
    phaseLabel: 'Day 2 — early stimulation',
    phaseSummary:
      "Your second injection tonight. Hormonally, the process is underway — but follicles haven't started growing visibly yet. Most women feel little different from yesterday. That's normal.",
    bubbleVariant: 'soft',
    bodyDoing: [
      'Follicles are still too small to detect — usually under 5mm',
      'Estradiol may begin a very slow rise — barely detectable',
      'Your hypothalamus is adjusting to the hormonal input',
      'The medication is building up in your system — consistency matters',
    ],
    normalToFeel: [
      'Similar to day 1 — no dramatic physical changes yet',
      'Injection site soreness may increase slightly',
      "Bloating is rare this early — if you feel it, it's likely anxiety",
      'Fatigue or mood shifts from hormonal input are normal',
    ],
    ohssLevel: 'low',
    ohssContext: {
      title: 'OHSS risk today — minimal',
      body:
        "Risk remains very low on day 2. Ovaries haven't responded yet. Continue normal activity — no restrictions needed.",
    },
    signsWorthNoting: [
      'Fever over 38°C/100.4°F at injection site → call clinic',
      'Missed injection — call clinic before next dose for guidance',
    ],
    motivationalQuote: 'Day 2. You showed up again. That matters.',
    easeTips: [
      'Rotate injection sites as your nurse showed — it keeps skin happier for the days ahead.',
      'Short walk or stretch after your shot can ease tension without strenuous exercise.',
      'Name one tiny reward after the injection (tea, show episode) so the habit feels kinder.',
    ],
    numbers: [
      { label: 'E2', value: 'Still near baseline — slow climb may start' },
      { label: 'LH', value: '<5 mIU/mL — suppressed' },
      { label: 'Follicles', value: '<5mm — not yet measurable' },
    ],
    articles: [
      {
        source: 'ASRM',
        title: 'Medications for Inducing Ovulation',
        author: 'ASRM',
        url: 'https://www.reproductivefacts.org/topics/topics-index/medications-for-inducing-ovulation/',
        min: 5,
      },
      {
        source: 'Mayo Clinic',
        title: 'In vitro fertilization (IVF)',
        author: 'Mayo Clinic Staff',
        url: 'https://www.mayoclinic.org/tests-procedures/in-vitro-fertilization/about/pac-20384716',
        min: 6,
      },
      {
        source: 'ReproductiveFacts.org',
        title: 'Ovarian Stimulation',
        author: 'ASRM',
        url: 'https://www.reproductivefacts.org/topics/topics-index/ovarian-stimulation/',
        min: 5,
      },
    ],
  },
  3: {
    novaGuidance:
      'Stim day 3 — follicles may start becoming visible on ultrasound in the next day or two. E2 should begin rising.',
    headerSubtitle: 'Early recruitment — scans may start soon.',
    phaseLabel: 'Day 3 — follicles beginning to recruit',
    phaseSummary:
      "By today, early follicles are beginning to respond — though they're still very small. If you have a day 3–5 baseline or early check scan, your clinic is looking for early recruitment and baseline E2 rise.",
    bubbleVariant: 'soft',
    bodyDoing: [
      'Small antral follicles beginning to respond to FSH stimulation',
      'Estradiol starting to rise — clinic may check E2 if you have an early scan',
      'Ovaries may begin to feel slightly heavier — normal early sign',
      'Multiple follicles competing to grow — your protocol is designed for this',
    ],
    normalToFeel: [
      'Very mild pelvic awareness — not pain, just awareness',
      'Continued injection site fatigue',
      'Emotional sensitivity as estrogen begins to rise',
      'Some women notice nothing different yet — also completely normal',
    ],
    ohssLevel: 'low',
    ohssContext: {
      title: 'OHSS risk today — low',
      body:
        'Still low risk. Follicles are too small to cause concern. Your clinic will start tracking response at your first scan.',
    },
    signsWorthNoting: [
      'Sharp or one-sided pelvic pain → call clinic',
      'No response confirmed at first scan → clinic will adjust protocol',
    ],
    motivationalQuote: "The follicles are responding. You can't feel it yet, but it's happening.",
    easeTips: [
      'Keep sleep as regular as you can — hormones love a steady rhythm.',
      'Sip water through the day; mild hydration supports how you feel on meds.',
      'If a scan is coming up, jot one question so you do not forget it in the room.',
    ],
    numbers: [
      { label: 'E2', value: 'Beginning rise — clinic-specific targets' },
      { label: 'LH', value: '<5 mIU/mL' },
      { label: 'Follicles', value: 'Often 3–6mm if visible' },
    ],
    articles: [
      {
        source: 'NHS (UK)',
        title: 'In vitro fertilisation (IVF)',
        author: 'NHS',
        url: 'https://www.nhs.uk/conditions/ivf/',
        min: 5,
      },
      {
        source: 'Co:Fertility / Extend Fertility',
        title: 'Estradiol (E2) Levels in Egg Freezing: What Your Numbers Really Mean',
        author: 'Extend Fertility Team',
        url: 'https://www.cofertility.com/freeze-learn/understanding-estradiol-e2-numbers-in-egg-freezing',
        min: 7,
      },
      {
        source: 'ReproductiveFacts.org',
        title: 'In Vitro Fertilization Treatment Journey',
        author: 'ASRM Practice Committee',
        url: 'https://www.reproductivefacts.org/patient-journeys/in-vitro-fertilization-treatment/',
        min: 6,
      },
    ],
  },
  4: {
    novaGuidance:
      'Stim day 4 — follicles are growing. E2 rising. Some physical awareness is normal now.',
    headerSubtitle: 'Measurable growth — bloating may begin.',
    phaseLabel: 'Day 4 — early follicle growth',
    phaseSummary:
      'Follicles are now measurable — typically 5–8mm. Estradiol is rising noticeably. You may start to feel your ovaries for the first time. This is normal and expected.',
    bubbleVariant: 'soft',
    bodyDoing: [
      'Follicles now measurable on ultrasound — 5–8mm typical',
      'E2 rising in step with follicle growth',
      'Ovaries enlarging slightly — you may feel mild heaviness',
      'Clinic monitoring E2 trajectory to adjust doses if needed',
    ],
    normalToFeel: [
      'Mild pelvic heaviness or pressure — especially when sitting',
      'Bloating beginning — ovaries are growing',
      'Emotional intensity picking up with rising estrogen',
      'Tender lower abdomen, especially when pressing',
    ],
    ohssLevel: 'watch',
    ohssContext: {
      title: 'OHSS risk today — low to moderate',
      body:
        'Risk is low but beginning to be trackable. Clinic will assess at first scan. Stay hydrated — 2–3L fluid daily from now.',
    },
    signsWorthNoting: [
      'Significant bloating this early → mention at next scan',
      'Sudden sharp pain → stop activity and call clinic',
    ],
    motivationalQuote: "They're growing. This is working.",
    easeTips: [
      'Loosen your waistband or switch to soft pants — comfort beats squeezing.',
      'Small, frequent meals can sit better than one heavy plate as bloating starts.',
      'A few minutes of slow breathing after work helps when estrogen feels intense.',
    ],
    numbers: [
      { label: 'E2', value: 'Rising — often 100–400+ pg/mL (wide range)' },
      { label: 'LH', value: '<2 mIU/mL — stay suppressed' },
      { label: 'Follicles', value: '5–8mm typical' },
    ],
    articles: [
      {
        source: 'Alex Robles MD',
        title: 'Estradiol Levels in IVF: What Is A Good Level & What Can You Expect?',
        author: 'Dr. Alex Robles, REI',
        url: 'https://alexroblesmd.com/estradiol-levels-ivf/',
        min: 6,
      },
      {
        source: 'ASRM',
        title: 'Prevention of Moderate and Severe OHSS: A Guideline (2023)',
        author: 'ASRM Practice Committee',
        url: 'https://www.asrm.org/practice-guidelines/practice-committee-documents/prevention-of-moderate-and-severe-ohss-a-guideline/',
        min: 4,
      },
      {
        source: 'ReproductiveFacts.org',
        title: 'Ovarian Hyperstimulation Syndrome (OHSS)',
        author: 'ASRM Patient Fact Sheet',
        url: 'https://www.reproductivefacts.org/topics/topics-index/ovarian-hyperstimulation-syndrome/',
        min: 4,
      },
    ],
  },
  5: {
    novaGuidance:
      "Stim day 5 — you're likely having or just had your first monitoring scan. Follicles are in active recruitment.",
    headerSubtitle: 'First monitoring scan window — dose adjustments common.',
    phaseLabel: 'Day 5 — first monitoring scan zone',
    phaseSummary:
      'Most protocols include a scan around day 5–6. Your clinic is checking how many follicles are responding and how your E2 is tracking. They may adjust your dose based on what they see.',
    bubbleVariant: 'soft',
    bodyDoing: [
      'Follicles typically 7–10mm — clearly visible on ultrasound',
      'E2 rising significantly — clinic using this to guide dosing',
      'Ovaries noticeably enlarged — physical awareness increases',
      'Protocol may be adjusted today based on response',
    ],
    normalToFeel: [
      'Moderate bloating — jeans may feel tight',
      'Pelvic pressure and heaviness most of the day',
      'Mood fluctuations as E2 rises sharply',
      'Fatigue — your body is working hard',
    ],
    ohssLevel: 'watch',
    ohssContext: {
      title: 'OHSS risk today — moderate (track AFC)',
      body:
        'Risk rises with follicle count and E2. AFC of 12 puts you in a watch zone — clinic will monitor closely. Prioritize hydration.',
    },
    signsWorthNoting: [
      'E2 rising faster than expected → clinic may reduce dose',
      'Fewer follicles than expected → clinic may increase dose',
      'Significant pain or nausea → call clinic same day',
    ],
    motivationalQuote:
      "The scan shows what you can't feel. Trust the process and the data.",
    easeTips: [
      'Bring your medication list to monitoring — it saves back-and-forth at the desk.',
      'Pack a water bottle with electrolytes if your clinic encourages them.',
      'Schedule a little buffer after the appointment; rushing adds stress you do not need.',
    ],
    numbers: [
      { label: 'E2 (estradiol)', value: '300–900 pg/mL typical' },
      { label: 'LH', value: '<2 mIU/mL — should stay suppressed' },
      { label: 'Follicles', value: '7–11mm — active growth' },
    ],
    articles: [
      {
        source: 'Co:Fertility / Extend Fertility',
        title: 'Estradiol (E2) Levels in Egg Freezing: What Your Numbers Really Mean',
        author: 'Extend Fertility Team',
        url: 'https://www.cofertility.com/freeze-learn/understanding-estradiol-e2-numbers-in-egg-freezing',
        min: 7,
      },
      {
        source: 'Alex Robles MD',
        title: 'Estradiol Levels in IVF: What Is A Good Level & What Can You Expect?',
        author: 'Dr. Alex Robles, REI',
        url: 'https://alexroblesmd.com/estradiol-levels-ivf/',
        min: 6,
      },
      {
        source: 'ASRM',
        title: 'Prevention of Moderate and Severe OHSS: A Guideline (2023)',
        author: 'ASRM Practice Committee',
        url: 'https://www.asrm.org/practice-guidelines/practice-committee-documents/prevention-of-moderate-and-severe-ohss-a-guideline/',
        min: 4,
      },
    ],
  },
  6: {
    novaGuidance:
      'Stim day 6 — active growth phase. Your antagonist may start today if on an antagonist protocol. Hydration matters.',
    headerSubtitle: 'Mid-stim — antagonist window; E2 climbing fast.',
    phaseLabel: 'Day 6 — active growth phase',
    phaseSummary:
      "Follicles are in active growth. If you're on an antagonist protocol, Ganirelix or Cetrotide often starts around day 5–7 to prevent a premature LH surge. E2 continues to climb.",
    bubbleVariant: 'soft',
    bodyDoing: [
      'Follicles 9–12mm — growing 1–2mm per day',
      'E2 often between 500–1500 pg/mL depending on follicle count',
      'Antagonist medication preventing premature LH surge',
      'Physical symptoms increasing — ovaries significantly enlarged',
    ],
    normalToFeel: [
      'Noticeable bloating — loose clothing recommended',
      'Pelvic pressure, especially after walking or standing',
      'Increased appetite or nausea — both common',
      'Breast tenderness from rising estrogen',
    ],
    ohssLevel: 'watch',
    ohssContext: {
      title: 'OHSS risk today — moderate, monitor closely',
      body:
        'With AFC 12, risk is meaningful now. Clinic watching E2 and follicle count closely. Drink electrolyte-rich fluids. Limit intense exercise.',
    },
    signsWorthNoting: [
      'E2 over 3,000 pg/mL → clinic may coast or trigger early',
      'Significant weight gain (2+ lbs overnight) → call clinic',
      'Difficulty breathing or urinating → emergency — call immediately',
    ],
    motivationalQuote: 'Day 6. Halfway through stims. You are doing this.',
    easeTips: [
      'Keep movement gentle — short walks beat HIIT while ovaries are enlarging.',
      'Salt your food normally unless your team said otherwise; pair fluids with food when you can.',
      'Say no to one non-essential task today — your body is running a big job already.',
    ],
    numbers: [
      { label: 'E2', value: 'Often 600–1,800+ pg/mL (protocol-dependent)' },
      { label: 'LH', value: 'Should stay low on antagonist' },
      { label: 'Lead follicles', value: 'Often ~9–13mm' },
    ],
    articles: [
      {
        source: 'ReproductiveFacts.org',
        title: 'Ovarian Stimulation',
        author: 'ASRM',
        url: 'https://www.reproductivefacts.org/topics/topics-index/ovarian-stimulation/',
        min: 5,
      },
      {
        source: 'ASRM',
        title: 'Ovarian Hyperstimulation Syndrome (OHSS)',
        author: 'ASRM Patient Fact Sheet',
        url: 'https://www.reproductivefacts.org/topics/topics-index/ovarian-hyperstimulation-syndrome/',
        min: 4,
      },
      {
        source: 'Mayo Clinic',
        title: 'In vitro fertilization (IVF)',
        author: 'Mayo Clinic Staff',
        url: 'https://www.mayoclinic.org/tests-procedures/in-vitro-fertilization/about/pac-20384716',
        min: 6,
      },
    ],
  },
  7: {
    novaGuidance:
      'Stim day 7 — follicles approaching trigger range. Retrieval is getting close.',
    headerSubtitle: 'Peak stim approaching — trigger timing on the horizon.',
    phaseLabel: 'Day 7 — approaching peak stimulation',
    phaseSummary:
      'Lead follicles are often 13–16mm by day 7. Retrieval is typically 3–5 days away. Your clinic is calibrating the final push — close monitoring of E2 and follicle sizes guides trigger timing.',
    bubbleVariant: 'soft',
    bodyDoing: [
      'Lead follicles 13–16mm — approaching the 17–20mm trigger target',
      'E2 often 1,500–3,000+ pg/mL range',
      'Smaller follicles still catching up — clinic tracking cohort spread',
      'Body working at near-peak hormone levels',
    ],
    normalToFeel: [
      'Significant bloating and abdominal fullness',
      'Difficulty with exercise or fast walking — this is normal',
      'Strong emotional swings — peak estrogen does this',
      'Insomnia or vivid dreams are common at this stage',
    ],
    ohssLevel: 'elevated',
    ohssContext: {
      title: 'OHSS risk today — elevated, active watch',
      body:
        'With AFC 12 and rising E2, OHSS risk is real. Your clinic is watching. Drink 2–3L electrolyte fluid daily. Weigh yourself each morning — report 2+ lb overnight gain.',
    },
    signsWorthNoting: [
      'Sudden dramatic E2 spike → may delay trigger',
      'Follicle growth stalling → clinic may extend stims',
      'Severe pain or rapid bloat increase → call clinic today',
    ],
    motivationalQuote:
      "The finish line is close. Your ovaries are doing exactly what they're supposed to.",
    easeTips: [
      'Prop your feet up when you can — even five minutes lowers pelvic drag.',
      'Dim screens an hour before bed; stim nights often sleep lighter.',
      'Tell someone trusted you are in a peak week — you do not have to carry it quietly.',
    ],
    numbers: [
      { label: 'E2', value: 'Often 1,000–2,500+ pg/mL (varies widely)' },
      { label: 'LH', value: 'Suppressed on antagonist protocol' },
      { label: 'Lead follicles', value: 'Often ~14–17mm' },
    ],
    articles: [
      {
        source: 'ReproductiveFacts.org',
        title: 'Ovarian Stimulation',
        author: 'ASRM',
        url: 'https://www.reproductivefacts.org/topics/topics-index/ovarian-stimulation/',
        min: 5,
      },
      {
        source: 'ASRM',
        title: 'Ovarian Hyperstimulation Syndrome (OHSS)',
        author: 'ASRM Patient Fact Sheet',
        url: 'https://www.reproductivefacts.org/topics/topics-index/ovarian-hyperstimulation-syndrome/',
        min: 4,
      },
      {
        source: 'NHS (UK)',
        title: 'In vitro fertilisation (IVF)',
        author: 'NHS',
        url: 'https://www.nhs.uk/conditions/ivf/',
        min: 5,
      },
    ],
  },
  8: {
    novaGuidance:
      "Stim day 8, antagonist protocol, AFC 12 — your follicles are in the active growth phase. Here's what's happening in your body and what's normal to feel today.",
    headerSubtitle: 'Peak stim — OHSS awareness and hydration matter.',
    phaseLabel: 'Day 8 — active growth phase',
    phaseSummary:
      'Multiple follicles are growing together. E2 reflects total follicle mass — your clinic uses ultrasound and labs to balance growth with safety. Antagonist meds help prevent an early LH surge.',
    bubbleVariant: 'soft',
    bodyDoing: [
      'Follicles recruit estrogen — E2 often rises quickly at this stage',
      'Ovaries enlarge — you may feel heaviness or tightness in the lower abdomen',
      'Your care team may adjust doses based on response',
      'Trigger is planned when follicle size and labs align — often in the next 1–3 days',
    ],
    normalToFeel: [
      'Bloating, pelvic pressure, clothes feeling tight at the waist',
      'Mild nausea or reflux — discuss with clinic if persistent',
      'Emotional intensity — peak stim hormones affect mood significantly',
      'Low energy and difficulty with strenuous movement',
    ],
    ohssLevel: 'watch',
    ohssContext: {
      title: 'OHSS watch — AFC 12',
      body:
        'Higher follicle counts can mean higher estrogen and more fluid shift after trigger. Hydrate, weigh yourself if your clinic asks, and call with worsening symptoms.',
    },
    signsWorthNoting: [
      'E2 over 4,000 pg/mL → clinic may adjust trigger or coast',
      'Rapid weight gain, decreased urination → call clinic immediately',
      'Severe abdominal pain → do not wait — call now',
    ],
    motivationalQuote: "Day 8. Almost there. The discomfort is the proof it's working.",
    easeTips: [
      'Eat smaller portions more often if fullness hits early — easier on a tight belly.',
      'Keep fluids steady through the day instead of chugging late at night.',
      'If you weigh yourself for the clinic, do it same time, same scale, before breakfast.',
    ],
    numbers: [
      { label: 'E2', value: 'Wide range — your clinic interprets against your scan' },
      { label: 'Follicles', value: 'Often several in the 14–18mm range' },
      { label: 'LH', value: 'Should remain low on antagonist' },
    ],
    articles: [
      {
        source: 'ReproductiveFacts.org',
        title: 'Ovarian Hyperstimulation Syndrome (OHSS)',
        author: 'ASRM',
        url: 'https://www.reproductivefacts.org/topics/topics-index/ovarian-hyperstimulation-syndrome/',
        min: 4,
      },
      {
        source: 'Mayo Clinic',
        title: 'In vitro fertilization (IVF)',
        author: 'Mayo Clinic Staff',
        url: 'https://www.mayoclinic.org/tests-procedures/in-vitro-fertilization/about/pac-20384716',
        min: 6,
      },
      {
        source: 'ASRM',
        title: 'Medications for Inducing Ovulation',
        author: 'ASRM',
        url: 'https://www.reproductivefacts.org/topics/topics-index/medications-for-inducing-ovulation/',
        min: 5,
      },
    ],
  },
  9: {
    novaGuidance:
      'Stim day 9 — trigger may be imminent. Lead follicles likely 18–20mm. E2 at or near peak.',
    headerSubtitle: 'Trigger window — instructions will be explicit; set alarms.',
    phaseLabel: 'Day 9 — trigger window approaching',
    phaseSummary:
      'By day 9, most lead follicles are approaching the 17–20mm target. Your clinic may trigger tonight or in the next day. Final scan confirms readiness — cohort size and E2 guide the decision.',
    bubbleVariant: 'soft',
    bodyDoing: [
      'Lead follicles 17–20mm — in or near trigger range',
      'E2 at or near cycle peak',
      'Smaller follicles continuing to catch up',
      'Final protocol adjustments — trigger timing being determined',
    ],
    normalToFeel: [
      'Maximum bloating and pelvic fullness of the cycle',
      'Walking may feel uncomfortable — rest is appropriate',
      'High emotional intensity — this is the hardest physical day for many',
      'Difficulty sleeping from discomfort and anticipation',
    ],
    ohssLevel: 'elevated',
    ohssContext: {
      title: 'OHSS risk — peak watch zone',
      body:
        'Highest risk period is around trigger and the days following. Report any rapid weight gain, difficulty breathing, or decreased urination immediately.',
    },
    signsWorthNoting: [
      'Trigger shot timing — do not miss the window',
      'Coasting (dose reduction) may be recommended if E2 is very high',
      'Any severe symptoms tonight → call clinic before trigger',
    ],
    motivationalQuote: 'The hardest part is almost over. Retrieval is so close.',
    easeTips: [
      'Double-check trigger instructions on paper — alarms plus a calendar block help.',
      'Clear your evening if you can; rest is medicine this close to trigger.',
      'Line up easy foods (broth, yogurt, toast) if big meals feel like too much.',
    ],
    numbers: [
      { label: 'E2', value: 'Often highest pre-trigger — clinic-specific cutoffs' },
      { label: 'Lead follicles', value: 'Often ~16–20mm depending on clinic' },
      { label: 'LH', value: 'Must stay suppressed until trigger plan' },
    ],
    articles: [
      {
        source: 'ReproductiveFacts.org',
        title: 'The IVF Process',
        author: 'ASRM',
        url: 'https://www.reproductivefacts.org/topics/topics-index/in-vitro-fertilization-ivf/',
        min: 6,
      },
      {
        source: 'ReproductiveFacts.org',
        title: 'Egg Retrieval',
        author: 'ASRM Patient Fact Sheet',
        url: 'https://www.reproductivefacts.org/topics/topics-index/egg-retrieval/',
        min: 4,
      },
      {
        source: 'ASRM',
        title: 'Patient Guide: Medications for Inducing Ovulation',
        author: 'ASRM',
        url: 'https://www.reproductivefacts.org/globalassets/rfs/pdf/informing_patients/ovulation-drugs-booklet.pdf',
        min: 8,
      },
    ],
  },
  10: {
    novaGuidance:
      'Stim day 10 — trigger shot likely last night or tonight. Retrieval is typically 36 hours after trigger.',
    headerSubtitle: 'Trigger shot or last stim day — follow instructions exactly.',
    phaseLabel: 'Day 10 — trigger and retrieval approach',
    phaseSummary:
      'Trigger shot (hCG or Lupron) is given 36 hours before retrieval. After trigger, stim injections stop. Your follicles are maturing for collection. This is the final countdown.',
    bubbleVariant: 'trigger',
    bodyDoing: [
      'Follicles maturing after trigger — final size 18–22mm',
      'Ovaries at maximum size — peak physical discomfort',
      'No more gonadotropin injections — protocol complete',
      'Body preparing for the egg release signal from trigger',
    ],
    normalToFeel: [
      'Intense pelvic pressure and bloating — peak of cycle',
      'Fatigue and low energy — rest as much as possible',
      'Emotional mix of relief and anxiety about retrieval',
      'Difficulty eating full meals — small portions recommended',
    ],
    ohssLevel: 'elevated',
    ohssContext: {
      title: 'OHSS risk — highest period',
      body:
        "Risk peaks in the 3–5 days following trigger. Drink electrolyte fluids consistently. Weigh yourself daily. Post-retrieval is when OHSS develops if it's going to.",
    },
    signsWorthNoting: [
      'Trigger timing is critical — set multiple alarms',
      'Post-trigger bloat increase is normal — monitoring continues',
      'Any breathing difficulty or severe pain → call immediately',
    ],
    motivationalQuote: 'Trigger done. Rest now. Tomorrow begins the next chapter.',
    easeTips: [
      'Follow NPO rules exactly as written — set a phone reminder the night before retrieval.',
      'Lay out loose clothes and slip-on shoes; you will be glad after sedation.',
      'Keep your partner or ride on standby with the clinic address pinned in messages.',
    ],
    numbers: [
      { label: 'Timing', value: 'Retrieval ~34–36h after trigger (clinic-specific)' },
      { label: 'E2', value: 'Often high — clinic decides if safe to proceed' },
      { label: 'Follicles', value: 'Target sizes per your protocol' },
    ],
    articles: [
      {
        source: 'ReproductiveFacts.org',
        title: 'The IVF Process',
        author: 'ASRM',
        url: 'https://www.reproductivefacts.org/topics/topics-index/in-vitro-fertilization-ivf/',
        min: 6,
      },
      {
        source: 'ASRM',
        title: 'Patient Guide: Medications for Inducing Ovulation',
        author: 'ASRM',
        url: 'https://www.reproductivefacts.org/globalassets/rfs/pdf/informing_patients/ovulation-drugs-booklet.pdf',
        min: 8,
      },
      {
        source: 'ReproductiveFacts.org',
        title: 'Egg Retrieval',
        author: 'ASRM Patient Fact Sheet',
        url: 'https://www.reproductivefacts.org/topics/topics-index/egg-retrieval/',
        min: 4,
      },
    ],
  },
  11: {
    novaGuidance: "Retrieval day — today is the day. You've done everything right.",
    headerSubtitle: 'Retrieval day — procedure, recovery, fertilization timeline.',
    phaseLabel: 'Day 11 — retrieval day',
    phaseSummary:
      "Egg retrieval happens under light sedation. The procedure takes 20–30 minutes. You'll rest in recovery and go home the same day. Your fertilization report comes in 24–48 hours.",
    bubbleVariant: 'trigger',
    bodyDoing: [
      'Eggs retrieved transvaginally under ultrasound guidance',
      "Light sedation — you won't feel the procedure",
      'Recovery takes 1–2 hours at the clinic',
      'Rest today — no driving, no strenuous activity',
    ],
    normalToFeel: [
      'Cramping and spotting after procedure — normal',
      'Groggy from sedation for several hours',
      'Bloating may be worse today — ovaries at max size',
      'Emotional release — many women cry, laugh, or both',
    ],
    ohssLevel: 'watch',
    ohssContext: {
      title: 'OHSS watch — retrieval day',
      body:
        'Ovarian hyperstimulation develops in the days after retrieval, not before. Watch for worsening bloat, pain, or decreased urination in the next 3–5 days.',
    },
    signsWorthNoting: [
      'Fever over 38°C after procedure → call clinic',
      'No eggs retrieved → clinic will discuss next steps',
      'Severe pain not relieved by Tylenol → call clinic',
    ],
    motivationalQuote: 'You made it to retrieval. Whatever the number — you showed up completely.',
    easeTips: [
      'Sip water and electrolytes as your team allows — steady beats chugging.',
      'Use a heating pad on low for cramps only if your clinic said it is okay.',
      'Keep the day unscheduled beyond rest — your body just did a lot.',
    ],
    numbers: [
      { label: 'Retrieval prep', value: 'NPO and arrival per clinic card' },
      { label: 'Medications', value: 'Only as listed — no extras without asking' },
      { label: 'Support', value: 'Driver required after sedation' },
    ],
    articles: [
      {
        source: 'ReproductiveFacts.org',
        title: 'Egg Retrieval',
        author: 'ASRM Patient Fact Sheet',
        url: 'https://www.reproductivefacts.org/topics/topics-index/egg-retrieval/',
        min: 4,
      },
      {
        source: 'MedlinePlus (NIH)',
        title: 'In vitro fertilization (IVF)',
        author: 'U.S. National Library of Medicine',
        url: 'https://medlineplus.gov/ency/article/007294.htm',
        min: 5,
      },
      {
        source: 'ACOG',
        title: 'In Vitro Fertilization (IVF)',
        author: 'The American College of Obstetricians and Gynecologists',
        url: 'https://www.acog.org/womens-health/faqs/in-vitro-fertilization',
        min: 4,
      },
    ],
  },
  12: {
    novaGuidance: 'Day after retrieval — fertilization report expected today or tomorrow. Rest and wait.',
    headerSubtitle: 'Fertilization call — ovaries recovering; hydrate and rest.',
    phaseLabel: 'Day 12 — fertilization report',
    phaseSummary:
      "Your clinic will call with the fertilization report — how many eggs fertilized normally (2PN). This is the first number in a series. Each step has normal attrition. Try not to project forward.",
    bubbleVariant: 'recovery',
    bodyDoing: [
      'Fertilized eggs (zygotes) dividing in the lab',
      'Clinic monitoring embryo development daily',
      'Your body recovering from retrieval and hormonal peak',
      'OHSS risk still active — monitor symptoms carefully',
    ],
    normalToFeel: [
      'Continued cramping and spotting — normal',
      'Bloating may actually increase today before decreasing',
      'Emotional vulnerability waiting for the call',
      'Physical exhaustion — rest is appropriate',
    ],
    ohssLevel: 'elevated',
    ohssContext: {
      title: 'OHSS watch — highest risk window',
      body:
        'Days 1–5 post-retrieval are peak OHSS risk. Report rapid weight gain (2+ lbs/day), decreased urination, or difficulty breathing immediately.',
    },
    signsWorthNoting: [
      'Lower fertilization than expected → clinic will discuss',
      'OHSS symptoms developing → call clinic, do not wait',
      'Fever → call clinic immediately',
    ],
    motivationalQuote: 'The fertilization number is the beginning of the count — not the end.',
    easeTips: [
      'Protect your heart today — limit doom-scrolling and set a timer on lab-result threads.',
      'Protein-forward snacks support recovery without heavy meals.',
      'Step outside for two minutes of fresh air when worry loops start.',
    ],
    numbers: [
      { label: 'Eggs retrieved', value: 'Varies — maturity matters more than count alone' },
      { label: 'Procedure time', value: 'Often ~20–30 minutes' },
      { label: 'Recovery', value: 'Clinic-specific observation before discharge' },
    ],
    articles: [
      {
        source: 'ReproductiveFacts.org',
        title: 'Egg Retrieval',
        author: 'ASRM Patient Fact Sheet',
        url: 'https://www.reproductivefacts.org/topics/topics-index/egg-retrieval/',
        min: 5,
      },
      {
        source: 'ASRM',
        title: 'What is IVF?',
        author: 'ASRM',
        url: 'https://www.reproductivefacts.org/topics/topics-index/in-vitro-fertilization-ivf/',
        min: 5,
      },
      {
        source: 'ACOG',
        title: 'Assisted Reproductive Technology',
        author: 'The American College of Obstetricians and Gynecologists',
        url: 'https://www.acog.org/womens-health/faqs/assisted-reproductive-technology',
        min: 4,
      },
    ],
  },
  13: {
    novaGuidance: 'Day 13 — embryos are growing. Day 3 or day 5 check coming.',
    headerSubtitle: 'Embryo culture — physical recovery continues.',
    phaseLabel: 'Day 13 — embryo development',
    phaseSummary:
      'Embryos are dividing. Day 3 embryos are typically 6–8 cells. Most clinics grow to day 5 blastocyst before transfer or freezing. Normal attrition between fertilization and blast is expected.',
    bubbleVariant: 'recovery',
    bodyDoing: [
      'Embryos developing toward day 3 (6–8 cell) stage',
      'Lab monitoring growth and grading quality',
      'Physical recovery continuing — most women feel better today',
      'OHSS still possible — symptoms should be plateauing or improving',
    ],
    normalToFeel: [
      'Physical symptoms easing — bloating slowly reducing',
      'Emotional intensity of waiting',
      'Appetite returning as hormones normalize',
      'Energy slowly returning',
    ],
    ohssLevel: 'watch',
    ohssContext: {
      title: 'OHSS watch — day 3 post-retrieval',
      body:
        'If OHSS was going to develop, symptoms are often clearest now. Improving bloat is a good sign. If worsening — call clinic.',
    },
    signsWorthNoting: [
      'Worsening symptoms after day 2 → call clinic',
      'No embryo update from clinic → call proactively',
    ],
    motivationalQuote: "They're growing in the lab. You've done your part.",
    numbers: [
      { label: 'Fertilization report', value: 'Timing varies — usually within 24–48h' },
      { label: 'Embryo updates', value: 'Day 3 and/or Day 5 per clinic' },
      { label: 'Activity', value: 'Light only until clinic clears you' },
    ],
    articles: [
      {
        source: 'ReproductiveFacts.org',
        title: 'Embryo Development and Grading',
        author: 'ASRM',
        url: 'https://www.reproductivefacts.org/topics/topics-index/embryo-development/',
        min: 5,
      },
      {
        source: 'ASRM',
        title: 'In Vitro Fertilization: IVF',
        author: 'ASRM',
        url: 'https://www.reproductivefacts.org/topics/topics-index/in-vitro-fertilization-ivf/',
        min: 6,
      },
      {
        source: 'ReproductiveFacts.org',
        title: 'The Two-Week Wait',
        author: 'ASRM Patient Fact Sheet',
        url: 'https://www.reproductivefacts.org/topics/topics-index/the-two-week-wait/',
        min: 3,
      },
    ],
  },
  14: {
    novaGuidance:
      'Day 14 — blastocyst development window. The embryo number may change today.',
    headerSubtitle: 'Blast stage — lab call may update counts and grades.',
    phaseLabel: 'Day 14 — blastocyst stage',
    phaseSummary:
      "Day 5 is blastocyst day — the most predictive stage for implantation. Not all day 3 embryos reach blast, and that's normal. Clinic will report blast count and grades. Remaining embryos may be biopsied for PGT-A.",
    bubbleVariant: 'recovery',
    bodyDoing: [
      'Blastocysts forming — inner cell mass and trophectoderm visible',
      'Clinic grading blast quality (e.g. 4AA, 3BB)',
      'Embryos being biopsied for genetic testing if planned',
      'Successful blasts being vitrified (frozen) for future transfer',
    ],
    normalToFeel: [
      'Physical recovery mostly complete',
      'Emotional intensity around the blast count call',
      'Hope and anxiety in equal measure — this is normal',
      'Relief that the physical process is behind you',
    ],
    ohssLevel: 'low',
    ohssContext: {
      title: 'OHSS risk — resolving',
      body:
        'By day 5 post-retrieval, OHSS should be improving. If symptoms are still worsening — call clinic today. Frozen transfer means no pregnancy hCG to prolong symptoms.',
    },
    signsWorthNoting: [
      'Zero blasts → clinic will discuss options and timeline',
      'PGT-A results take 7–14 days — plan for the wait',
      'Any continuing severe symptoms → follow up with clinic',
    ],
    motivationalQuote:
      "Whatever the blast number — you have more information now than you did before. That's the point.",
    easeTips: [
      'Ask your team when to expect PGT or freeze updates so the wait has edges.',
      'Celebrate surviving stim and retrieval — that was a real physical chapter.',
      'Book something gentle on the calendar (walk with a friend) so hope has company.',
    ],
    numbers: [
      { label: 'Embryo updates', value: 'Day 3 and/or Day 5 per clinic' },
      { label: 'Hormone meds', value: 'Per transfer or freeze-all plan' },
      { label: 'Follow-up', value: 'Nurse line hours and portal messages' },
    ],
    articles: [
      {
        source: 'ReproductiveFacts.org',
        title: 'Embryo Development and Grading',
        author: 'ASRM',
        url: 'https://www.reproductivefacts.org/topics/topics-index/embryo-development/',
        min: 5,
      },
      {
        source: 'ASRM',
        title: 'In Vitro Fertilization: IVF',
        author: 'ASRM',
        url: 'https://www.reproductivefacts.org/topics/topics-index/in-vitro-fertilization-ivf/',
        min: 6,
      },
      {
        source: 'ReproductiveFacts.org',
        title: 'The Two-Week Wait',
        author: 'ASRM Patient Fact Sheet',
        url: 'https://www.reproductivefacts.org/topics/topics-index/the-two-week-wait/',
        min: 3,
      },
    ],
  },
};

/** Infer icon row from copy — keeps signs as plain strings in data. */
export function signKindFromText(text) {
  const lower = text.toLowerCase();
  if (
    /\b(er now|emergency care|911|torsion)\b/.test(lower) ||
    /→\s*er\b/.test(lower) ||
    /seek emergency/.test(lower)
  ) {
    return 'er';
  }
  if (
    /→\s*call clinic/.test(text) ||
    /\bcall clinic\b/.test(lower) ||
    /\bcall before\b/.test(lower) ||
    /\bcall for\b/.test(lower) ||
    /\bcall or er\b/.test(lower) ||
    /\bclarify before\b/.test(lower) ||
    /\bcall immediately\b/.test(lower) ||
    /\bcall now\b/.test(lower) ||
    /\bcall today\b/.test(lower) ||
    /\bcall proactively\b/.test(lower) ||
    /\bcall per\b/.test(lower)
  ) {
    return 'call';
  }
  return 'ok';
}
